import { Subject } from 'rxjs';
import * as signalR from '@microsoft/signalr';
import { TimberService } from '../../logging/timber.service';

enum ConnectionState {
	Disconnected,
	Connecting,
	Connected,
	Disconnecting,
}

const MODULE = { module: 'signalR.service' };

export class BaseSignalR<T> {
	private readonly maxConnectionRetryDelay = 60000;
	private readonly initialRetryDelay = 5000;
	private readonly authRetryDelay = 3000;
	private readonly maxAuthRetries = 2;
	private readonly maxConnectionRetries = 5;

	private messageBroker: Subject<T> = new Subject<T>();
	private hubConnection: signalR.HubConnection;
	private connectionState: ConnectionState = ConnectionState.Disconnected;
	private reconnectCount = 0;
	private authRetryCount = 0;
	private networkRetryCount = 0;
	private isStarting = false;

	protected stopNotifications$: Subject<void> = new Subject<void>();
	protected messageBroker$ = this.messageBroker.asObservable();

	private static readonly instances = new Set<BaseSignalR<any>>();

	static async stopAll(): Promise<void> {
		const snapshot = Array.from(BaseSignalR.instances);
		BaseSignalR.instances.clear();
		await Promise.all(snapshot.map(instance => instance.stopNotifications()));
	}

	protected constructor(protected timberService: TimberService) { }

	async stopNotifications(): Promise<void> {
		this.timberService.debug(`[SignalR] stopNotifications. state: ${ConnectionState[this.connectionState]} | hubState: ${this.hubConnection?.state ?? 'no hub'}`, MODULE);
		BaseSignalR.instances.delete(this);
		this.isStarting = false;
		this.stopNotifications$.next();
		this.stopNotifications$ = new Subject<void>();
		await this.closeConnection();
	}

	protected async initMessages(
		hubUrl: string,
		channelName: string,
		accessTokenFactory: () => string | Promise<string>,
		queryParams?: any
	): Promise<void> {
		if (this.connectionState !== ConnectionState.Disconnected) {
			this.timberService.debug(`[SignalR] initMessages skipped — state: ${ConnectionState[this.connectionState]}`, MODULE);
			return;
		}

		const params = queryParams ? new URLSearchParams(queryParams).toString() : '';
		const fullUrl = params ? `${hubUrl}?${params}` : hubUrl;

		this.timberService.debug(`[SignalR] Initializing. hub: ${hubUrl} | channel: ${channelName} | params: ${params}`, MODULE);
		BaseSignalR.instances.add(this);

		this.hubConnection = new signalR.HubConnectionBuilder()
			.withUrl(fullUrl, { accessTokenFactory })
			.build();

		this.hubConnection.on(channelName, (message: T) => {
			this.timberService.trace(`[SignalR] Message received. channel: ${channelName}`, MODULE);
			this.messageBroker.next(message);
		});

		this.hubConnection.onclose(async (error) => {
			const previousState = this.connectionState;
			const connectionId = this.hubConnection.connectionId;
			this.timberService.debug(`[SignalR] onclose. connectionId: ${connectionId} | previousState: ${ConnectionState[previousState]} | error: ${error?.message ?? 'none'}`, MODULE);
			this.connectionState = ConnectionState.Disconnected;

			if (previousState === ConnectionState.Disconnecting) {
				this.timberService.debug(`[SignalR] Closed intentionally — not reconnecting.`, MODULE);
				this.reconnectCount = 0;
				this.networkRetryCount = 0;
				return;
			}

			this.reconnectCount++;
			this.timberService.info(`[SignalR] Reconnecting (attempt #${this.reconnectCount}). connectionId: ${connectionId}`, MODULE);
			this.connectionState = ConnectionState.Connecting;
			await this.startHub();
		});

		this.connectionState = ConnectionState.Connecting;
		await this.startHub();
	}

	private async closeConnection(): Promise<void> {
		if (!this.hubConnection) {
			this.timberService.trace(`[SignalR] closeConnection — no hub exists.`, MODULE);
			return;
		}

		const { connectionId, state: hubState } = this.hubConnection;
		this.connectionState = ConnectionState.Disconnecting;
		this.timberService.debug(`[SignalR] closeConnection. connectionId: ${connectionId} | hubState: ${hubState}`, MODULE);

		const isActive =
			hubState === signalR.HubConnectionState.Connected ||
			hubState === signalR.HubConnectionState.Connecting ||
			hubState === signalR.HubConnectionState.Reconnecting;

		if (isActive) {
			await this.hubConnection.stop();
			this.timberService.debug(`[SignalR] Hub stopped. connectionId: ${connectionId}`, MODULE);
		} else {
			this.connectionState = ConnectionState.Disconnected;
			this.timberService.trace(`[SignalR] Hub already stopped. connectionId: ${connectionId}`, MODULE);
		}
	}

	private async startHub(): Promise<void> {
		if (this.connectionState !== ConnectionState.Connecting) {
			this.timberService.trace(`[SignalR] startHub skipped — state: ${ConnectionState[this.connectionState]}`, MODULE);
			return;
		}

		if (this.isStarting) {
			this.timberService.trace(`[SignalR] startHub skipped — already starting`, MODULE);
			return;
		}

		this.isStarting = true;
		this.timberService.debug(`[SignalR] hub.start(). reconnectCount: ${this.reconnectCount}`, MODULE);

		try {
			await this.hubConnection.start();
		} catch (error) {
			this.isStarting = false;
			this.handleStartError(error);
			return;
		}

		this.isStarting = false;
		this.connectionState = ConnectionState.Connected;
		this.authRetryCount = 0;
		this.networkRetryCount = 0;
		const totalReconnects = this.reconnectCount;
		this.reconnectCount = 0;
		this.timberService.info(`[SignalR] Connected. connectionId: ${this.hubConnection.connectionId} | totalReconnects: ${totalReconnects}`, MODULE);
	}

	private handleStartError(error: any): void {
		const isAuthError = error?.statusCode === 401 || error?.statusCode === 403
			|| error?.message?.includes('401') || error?.message?.includes('403');

		if (isAuthError) {
			if (this.authRetryCount < this.maxAuthRetries) {
				this.authRetryCount++;
				this.timberService.warn(`[SignalR] Auth error — retrying after ${this.authRetryDelay}ms (attempt ${this.authRetryCount}/${this.maxAuthRetries}). error: ${error?.message}`, MODULE);
				this.startHubDeferred(this.authRetryDelay);
			} else {
				this.timberService.warn(`[SignalR] Auth error — max retries (${this.maxAuthRetries}) reached. Stopping. error: ${error?.message}`, MODULE);
				this.connectionState = ConnectionState.Disconnected;
				this.reconnectCount = 0;
				this.authRetryCount = 0;
			}
			return;
		}

		this.authRetryCount = 0;

		if (this.networkRetryCount >= this.maxConnectionRetries) {
			this.timberService.warn(`[SignalR] Max fast retries (${this.maxConnectionRetries}) reached. Continuing at ${this.maxConnectionRetryDelay}ms. error: ${error?.message}`, MODULE);
			this.startHubDeferred(this.maxConnectionRetryDelay);
			return;
		}

		this.networkRetryCount++;
		const delay = Math.min(this.initialRetryDelay * Math.pow(2, this.networkRetryCount - 1), this.maxConnectionRetryDelay);
		this.timberService.warn(`[SignalR] Connection failed — retrying in ${delay}ms (attempt ${this.networkRetryCount}/${this.maxConnectionRetries}). error: ${error?.message}`, MODULE);
		this.startHubDeferred(delay);
	}

	private startHubDeferred(delayInMilliseconds: number): void {
		setTimeout(async () => {
			if (this.connectionState === ConnectionState.Connecting) {
				this.timberService.debug(`[SignalR] Deferred retry. reconnectCount: ${this.reconnectCount}`, MODULE);
				await this.startHub();
			} else {
				this.timberService.trace(`[SignalR] Deferred retry cancelled — state: ${ConnectionState[this.connectionState]}`, MODULE);
			}
		}, delayInMilliseconds);
	}
}
